import {useState, useEffect, useCallback, useRef} from 'react';

import {nanoid} from '@reduxjs/toolkit';
import asyncStats from '@zoom/asynccomm-stats';
import hybrid from '@zoom/hybrid-jssdk';
import {Base64} from 'js-base64';
import {debounce} from 'lodash';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate, useLocation} from 'react-router-dom';

import {isSharedWithMe, isTeamAppt} from './apptAvailabilityUtils';
import {
  CLIENT_NOTIFICATIONS,
  CUSTOM_NOTIFICATIONS,
  FREE_TRIAL_CONDITION,
  LICENSE_INFO,
  MEET_INTEGRATION_TYPE,
  PAY_CHANNEL,
  PAYMENT_INTEGRATION_TYPE,
  SCOPE_TYPE,
  SUBMIT_FEEDBACK,
  ZOOM_WEB_DOMAIN,
} from './consts';
import {getCookie} from './CookieUtil';
import {global as Bus} from './EventBus';
import {initHybridConfig} from './initHybridAppBridge';
import ZoomSchedulerSDKClient from './zmschedulersdk';

import {
  addMeetIntegration, generatePaymentOnboardingURL, getUser, getWebUserInfo, patchIntegrationSettings,
} from 'Api/ZCalendar';
import {listAppointmentsForCalendar} from 'Store/HostAppointmentsStore';
import {listPendingForCalendar} from 'Store/HostBookingsStore';
import {validateUserConnections} from 'Store/HostConnectionHealthStore';
import {listWorkflowsForCalendar} from 'Store/HostWorkflowsStore';
import {
  actionHasRedirected,
  fetchUserHasLicense,
  listAccounts,
  updateFreeTrialInfo,
} from 'Store/IntegrationStore';
import {fetchManagedEvents} from 'Store/ManagedEventsStore';
import {snackbarActions} from 'Store/SnackbarStore';
import {
  compareSemVer,
  deferred,
  isBitSet,
  getVirtualUserEmail,
  isCciToken,
  isZoomieEmail,
  readJWTPayload,
  strEq,
} from 'Utils';
import {METRIC_NAME} from 'Utils/consts';

let token = (process.env.NODE_ENV === 'development') ?
  new URLSearchParams(window.location.search).get('tok') || '' :
  '';
let zakToken;
export let parentURL = null;
export let shouldBuyLicense = false;
export let hasBillingEditPermission = true;

let lastCallTokenTime = new Date().getTime();
// unlicensed eligible reverse free trial user will auto upgrade to licensed user
let isReverseFreeTrialUpgraded = false;
let isCciPage = false;
// request new token every hour
const DURATION = 3600 * 1000;
const schedulerDomain = window.location.origin || window.origin;

const searchParams = new URLSearchParams(window.location.search);
// injected by Web Team
export let canCreateZmail = (searchParams.get('setupZmail') || '') === 'true';
export const embedWidgetParams = searchParams.get('embedStyle') || '';

export const isInIframe = () => {
  try {
    return window.self !== window.top;
  } catch (e) {
    console.error(e);
    return true;
  }
};

export const isEuCookie = () => {
  const cluster = getCookie('zmail_cluster');
  try {
    if (cluster) {
      return cluster.toLowerCase().startsWith('ac70');
    }
  } catch {}
  return false;
};

export const isMobileApp = window.location?.href?.includes('/m/');

export const isFromO365 = window.location?.href?.indexOf('clientType=calFor365') > 0;
export const isFromMailClient = window.location?.href?.indexOf('from=mail-client') > 0;

// only O365 and Mail Client get token by SDK, others get token by API
const isGetTokenBySDK = isFromO365 || isFromMailClient;

const recheckLicense = searchParams.get('licenseChanged') === 'true';

const isEmbedMode = searchParams.get('embed') === 'true';
export const inWebPortal = isInIframe() && !isEmbedMode && !isMobileApp;

/**
 * Returns the initial system params when running in the client webview or null when not applicable
 *  * Can be used to read the initial values synchronously, but data may not be up to date
 * @return {Object|null}
 */
export const getInitialSystemParams = () => {
  // Desktop client webview
  if (!!window.__system_params__) {
    return window.__system_params__;
  }
  // Mobile client webview
  try {
    return (
      // iOS
      window.unifyConfig?.systemParam ??
      // Android env stores differently
      JSON.parse(window.android_config?.getSystemParam?.())
    );
  } catch {
    return null;
  }
};

// isInZoomMobileClient means Scheduler runs inside Zoom Mobile Client
export const inZoomMobileClient = getInitialSystemParams()?.deviceType === 'phone';

// inZoomClient means Scheduler runs inside Zoom Client
export const inZoomClient = !!window.__system_params__;
// styleInZoomClient means use client style design, can only be used for styles, not business logic
export const styleInZoomClient = !inWebPortal;
const initializeTokenRefresher = () => {
  setInterval(() => {
    const currentTime = new Date().getTime();
    if (currentTime - lastCallTokenTime > DURATION) {
      requestTokenFromPortal();
      asyncStats.report({metricName: METRIC_NAME.WEB_REFRESH_TOKEN, isImmediately: true});
    }
  }, 5000);
};
export const initializeToken = async (onComplete) => {
  const pathFirstPart = window.location.pathname.split('/')[1] || '';
  // user can use `${DOMAIN}/` or `${DOMAIN}/me/appts` to access the landing page entry
  const isLandingPageEntry = pathFirstPart === '' || pathFirstPart === 'me';
  const isLocalDevMode = window.location.host.startsWith('localhost');
  if (isGetTokenBySDK && typeof ZoomSchedulerSDKClient?.getSchedulerToken === 'function') {
    token = await ZoomSchedulerSDKClient.getSchedulerToken();
    if (token) {
      onComplete?.();
      return;
    }
  }
  if (!inWebPortal && !inZoomClient && !inZoomMobileClient && (!isLandingPageEntry || isLocalDevMode)) {
    onComplete?.();
    return;
  }

  initHybridConfig();

  requestParentURL().then(() => {
    isCciPage = !!(parentURL?.href.includes('/appointment/zcc'));
    requestLicenseFromPortal(recheckLicense).then(async (res) => {
      if (res.currentUserHasPermission === true || res.currentUserHasPermission === false) {
        hasBillingEditPermission = res.currentUserHasPermission;
      }
      let isUnlicensedUserFlow;
      if (res.trialing !== true && !isCciPage && !res.result) {
        isUnlicensedUserFlow = true;
        // if eligable for reverse free trial, call API to opt in
        // Note: Zoom mobile client users are not eligible
        if (!inZoomMobileClient && res.trialType === 1) {
          try {
            await optInReverseFreeTrial();
            // if opt-in success, re-enter the licensed user flow
            isUnlicensedUserFlow = false;
            isReverseFreeTrialUpgraded = true;
          } catch (err) {
            console.error('failed to opt-in', err);
          }
        }
      }
      // web will not issue a valid token for unlicensed user, so render purchase page directly
      if (isUnlicensedUserFlow) {
        shouldBuyLicense = true;
        onComplete?.();
        requestTokenFromPortal();
      } else {
        requestTokenFromPortal()
          .then(async () => {
            /**
             * In the client env when the user signs in using phone number, the token 'eml' can be
             *  their sign-in phone number.
             * In that event, we should fetch and set the loginEmailPatch to the user's actual email
            */
            const selfUser = getUserInfo(); // Assumes token has been set and can be read
            if (selfUser?.loginEmail && !selfUser?.loginEmail?.includes('@')) {
              try {
                const selfOrgUser = await getUser(selfUser.userId);
                loginEmailPatch = selfOrgUser.email;
                asyncStats.report({
                  metricName: METRIC_NAME.INITIALIZE_USER_CONTEXT,
                  description: 'success',
                });
              } catch (e) {
                console.error('Failed to retrieve user display loginEmail', e);
                asyncStats.report({
                  metricName: METRIC_NAME.INITIALIZE_USER_CONTEXT,
                  description: 'fail',
                });
              }
            }
            return;
          })
          .then(() => {
            asyncStats.report({
              metricName: inZoomClient ?
                METRIC_NAME.CLIENT_LOGIN :
                inZoomMobileClient ?
                  METRIC_NAME.CLIENT_MOBILE_LOGIN :
                  METRIC_NAME.WEB_LOGIN,
              isImmediately: true,
            });
            onComplete?.();
            // Zoom Client has auto refresh notification, no need to poll
            if (!inZoomClient && !inZoomMobileClient) {
              initializeTokenRefresher();
            }
          });
      }
    });
  });
};

const genericRequestFromPortal = (reqName, callbackName, params) => {
  let uuid;
  if (reqName === 'ajax') {
    uuid = nanoid();
    params.id = uuid;
  }
  const {promise, reject, resolve} = deferred();
  // web portal callback to iframe
  const handler = (e) => {
    if (e.data.type === callbackName) {
      if (e.data.type === 'ajaxResult') {
        if (e.data.id === uuid) {
          window.removeEventListener('message', handler);
          if (e.data.success) {
            resolve(e.data.result);
          } else {
            reject(e.data);
          }
        }
      } else {
        window.removeEventListener('message', handler);
        resolve(e.data);
      }
    }
  };
  window.addEventListener('message', handler);
  // request to web portal
  window.parent.postMessage(
    {
      type: reqName,
      params,
    },
    '*'
  );
  return promise;
};

const requestTokenFromPortal = async () => {
  lastCallTokenTime = new Date().getTime();
  if (isGetTokenBySDK && typeof ZoomSchedulerSDKClient?.getSchedulerToken === 'function') {
    token = await ZoomSchedulerSDKClient.getSchedulerToken();
    if (token) {
      return token;
    }
  }
  if (inZoomClient || inZoomMobileClient) {
    const {result} = await hybrid.bridge.getInstance().postJsMessage({
      functionName: 'calendarui_getzoombookingaccesstoken',
      params: {},
    });
    token = result.sak;
    if (!token) {
      // if an empty token is returned, the token needs to be refreshed forcibly
      await forceRefreshClientToken({
        zmailChange: true,
      });
    }
    zakToken = result.zak;
  } else if (!inWebPortal) {
    const host = window.location.host;
    let zoomDomain = '';
    if (host === 'scheduler.acqa.zoomdev.us') {
      zoomDomain = 'https://zoomdev.us';
    } else if (host === 'scheduler.zoom.us') {
      zoomDomain = 'https://zoom.us';
    } else if (host === 'scheduler.zoomdev.us') {
      zoomDomain = 'https://devep.zoomdev.us';
    }
    if (!zoomDomain) {
      if (process.env.NODE_ENV !== 'development') {
        alert(`Current env doesn't support direct access`);
      }
      return '';
    }
    const res = await fetch('/appointment/token', {
      credentials: 'include',
    });
    const textResponse = await res.text();
    // should go to Zoom login page
    if (res.redirected) {
      window.location.href = `${zoomDomain}/signin?continue=${window.location}`;
    } else {
      try {
        token = JSON.parse(textResponse).result;
      } catch (error) {
        // if parse token response error, it means user has no license, should go to web portal Scheduler
        window.location.href = `${zoomDomain}/appointment`;
      }
    }
  } else {
    const response = await genericRequestFromPortal('getToken', 'tokenResult');
    token = response.result;
  }
  asyncStats.setToken({token: `Bearer ${token}`});
  return token;
};

export const requestParentURL = async () => {
  try {
    // Scheduler has parent URL only when loaded via iframe
    if (inWebPortal) {
      const response = await genericRequestFromPortal('getUrl', 'urlResult');
      parentURL = new URL(response.result);
    }
  } catch (error) {}

  if (parentURL?.searchParams.get('action') === 'homepage') {
    // if jump from marketing page, hide Zoom web top right buttons
    const elementsToBeHide = [
      '#btnScheduleMeeting',
      '#btnJoinMeeting',
      '#dropdown-hostmeeting',
      '#dropdown-joinmeeting',
      '#btnWhiteboards',
    ];
    elementsToBeHide.forEach((selector) =>
      adjustParentPageStyle(selector, 'display', 'none')
    );
  }
};

/**
 * The parent web frame will cache the license result once on load. If the value changes, the parent will
 *  not update until a full reload occurs. If the latest data must be used, forceFetch can be passed to get
 *  the latest data.
 * @param {boolean?} forceFetch
 * @return {{result: boolean}}
 */
export const requestLicenseFromPortal = async (forceFetch=false) => {
  if (inZoomMobileClient) {
    return {result: true};
  }
  if (inZoomClient) {
    const clientVersion = window.__system_params__.version;
    // client free trial is supported since 6.0.10
    if (clientVersion && compareSemVer(clientVersion, '6.0.10') >= 0) {
      try {
        const {result: res} = await hybrid.bridge.getInstance().postJsMessage({
          functionName: 'calendarui_getloginuserprofile',
          params: {modules: ['webSettings', 'zoomAccount']},
        });
        let trialing;
        // zoomSchedulerFreeTrialStatus:
        // -1 -> invalid status
        //  0 -> is in free trial period
        //  1 -> eligible to apply for a free trial
        if (res.webSettings.zoomSchedulerFreeTrialStatus >= 0) {
          trialing = !res.webSettings.zoomSchedulerFreeTrialStatus;
        }
        const returnValue = {
          result: res.webSettings.isZoomSchedulerLicenseUser,
          freeTrialUrl: res.webSettings.zoomSchedulerTrialUrl,
          remainDays: res.webSettings.zoomSchedulerTrialRemainDays,
          trialing,
          trialType: res.webSettings.zoomSchedulerFreeTrialType + 1,
          isPaidAccount: res.zoomAccount.isPaidUser,
          enableFeedback: res.webSettings.enableFeedback,
        };
        // if auto-upgraded to licensed user, manually patch the license status
        if (isReverseFreeTrialUpgraded) {
          returnValue.result = true;
          returnValue.trialing = true;
          returnValue.trialType = 1;
        }
        return returnValue;
      } catch (error) {
        console.error('failed to get free trial info');
      }
    }
    return {result: true};
  }
  if (!inWebPortal || forceFetch) {
    // Get the very latest licensing data from the api
    try {
      const res = await fetch('/appointment/info', {
        credentials: 'include',
      });
      const info = await res.json();
      canCreateZmail = info.canSetupZmail;
      return {
        ...info,
        isPaidAccount: info.userType === 2,
        result: info.userHasZoomScheduler,
      };
    } catch (error) {
      console.error('get scheduler info from web error', error);
    }
    if (!forceFetch) {
      return {result: true};
    }
  }
  // Return the cached license status from the time at which the parent web frame was first loaded
  const res = await genericRequestFromPortal('getLicense', 'licenseResult');
  // if auto-upgraded to licensed user, manually patch the license status
  if (isReverseFreeTrialUpgraded) {
    res.result = true;
    res.trialing = true;
  }
  return res;
};

export const getWebUserInfoByApi = async () => {
  try {
    const info = await getWebUserInfo();
    canCreateZmail = info.canSetupZmail;
    return {
      ...info,
      isPaidAccount: info.userType === 2,
      result: info.userHasZoomScheduler,
    };
  } catch (err) {
    console.error('get web user info by api error', err);
  }
  return {};
};

export const getToken = () => {
  if (shouldBuyLicense) {
    return;
  }
  // for Concat Center temporary test use (only on non-prod env)
  if (parentURL?.hostname.endsWith('zoomdev.us') && parentURL?.searchParams.get('tok')) {
    return parentURL.searchParams.get('tok');
  }
  const locationURL = new URL(window.location.href);
  if (window.location.hostname.endsWith('zoomdev.us') && locationURL.searchParams.get('tok')) {
    return locationURL.searchParams.get('tok');
  }
  return token || (process.env.NODE_ENV === 'development'? require('../mocks/testToken.json')?.token: '');
};

export const getAid = () => {
  try {
    const tokenPaylod = readJWTPayload(getToken());
    return tokenPaylod.aid;
  } catch (error) {}
};

/**
 * In the client env when the user signs in using phone number, the token 'eml' is their sign-in phone number.
 * In that event, we should fetch and set the loginEmailPatch to the user's actual email
 */
let loginEmailPatch = undefined;
/**
 * Get user info from the token or null if token invalid
 * @return {?SelfUserInfo}
 */
export const getUserInfo = () => {
  // if user doesn't have license, Web team will not issue token, no need to parse token payload
  if (shouldBuyLicense) {
    return {
      license: LICENSE_INFO.ZOOM_SCHEDULER,
      loginEmail: '',
      calendarId: '',
      isCciAccount: false,
      isDomainAdmin: false,
      isManagePaymentGranted: false,
      isZoomie: false,
      zmail: '',
      userId: '',
      userName: '',
      accountId: '',
      paramsValid: true,
    };
  }
  try {
    const jwtPayload = readJWTPayload(getToken());
    if (!jwtPayload.eml && !jwtPayload.zmail) {
      console.error('missing token eml');
      return null;
    }
    const calendarId = formatCalendarId(jwtPayload);
    // [ZOOM-661401] support account admin assign admin privileges for Zoom Scheduler to a user
    const adminPrivilegeGranted = jwtPayload.ty === 8 && isBitSet(jwtPayload.perm_collection, 9);
    const isManagePaymentGranted = jwtPayload.ty === 8 && isBitSet(jwtPayload.perm_collection, 10);
    // token issued by web portal use "0/1/2" represent owner/admin/normal
    const isDomainAdmin = ['domainAdmin', 'domainOwner', '0', '1'].includes(jwtPayload.rol) || adminPrivilegeGranted;
    // If the loginEmailPatch was set, use it; otherwise, use the jwtPayload.eml as usual.
    const eml = loginEmailPatch ?? jwtPayload.eml;
    return {
      language: jwtPayload.locl,
      license: jwtPayload.lic,
      loginEmail: eml,
      calendarId,
      isCciAccount: jwtPayload.ty === 10,
      isDomainAdmin,
      isManagePaymentGranted,
      isZoomie: isZoomieEmail(eml),
      zmail: jwtPayload.zmail,
      userId: jwtPayload.uid,
      userName: jwtPayload.un,
      accountId: jwtPayload.aid,
      paramsValid: true,
    };
  } catch (e) {
    console.error('could not parse token', e);
    Bus.emit(CUSTOM_NOTIFICATIONS.onTokenExpired);
    return null;
  }
};

/**
 * Convert self info to the scope format
 * @param {SelfUserInfo} [selfUserInfo]
 * @return {Scope}
 */
export const selfToScope = (selfUserInfo) => {
  return {
    id: selfUserInfo?.userId,
    name: selfUserInfo?.userName,
    email: selfUserInfo?.loginEmail,
    type: SCOPE_TYPE.USER,
  };
};

export const isPhoneDevice = () => {
  // is mobile platform and has narrow screen < 480px (in order to exclude Pad)
  if (
    /mobile/i.test(navigator.userAgent) &&
    window.matchMedia('only screen and (max-width: 480px)').matches
  ) {
    return true;
  }
  return false;
};


export const useRedirect = () => {
  const hasActionRedirected = useSelector((state) => state.integrationState.hasActionRedirected);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const action = parentURL?.searchParams.get('action') || '';
  const eventIdFilter = parentURL?.searchParams.get('eventId') || '';
  const listAccountState = useSelector((state) => state.integrationState.loadingState.listAccountState);
  const haveNoAccount = useSelector((state) => state.integrationState.accounts.value.length === 0);
  const skipBindCalendar = useSelector((state) => state.integrationState.skipBindCalendar);

  const {pathname} = useLocation();
  useEffect(() => {
    dispatch(fetchUserHasLicense());
    // initially pull accounts to decide whether it's a new user or not (redirect callback doesn't need)
    !pathname.startsWith('/connect_callback') && dispatch(listAccounts({
      query: {
        // only get connected accounts(without calendars under the accounts) to speed up the FMP(First Meaningful Paint)
        skipCalendarList: 'true',
      },
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    if (isPhoneDevice()) {
      return navigate('/me/mobile', {replace: true});
    }
    // unlicensed user will not have token, so isCciToken() will not work
    if (shouldBuyLicense) {
      return navigate('/me/purchase', {replace: true});
    }
    if (!isCciToken() && !skipBindCalendar &&
      listAccountState === 'fulfilled' && haveNoAccount && !pathname.startsWith('/me/welcome')) {
      return navigate('/me/welcome');
    }
    // TODO no need to redirect anymore
    // // if in welcome page user has finished binding, should send user back to the original destination
    // if (!haveNoAccount && pathname.startsWith('/me/welcome')) {
    //   return navigate(-1);
    // }
    if (action.startsWith('settings-') && !hasActionRedirected) {
      // action should only works for the first route
      dispatch(actionHasRedirected());
      // support client jump to settings-profile / settings-account / settings-integrations / settings-payment
      const dest = action.substring(action.indexOf('-')+1);
      if (dest === 'integrations') {
        return navigate(`/me/${dest}`);
      } else {
        return navigate(`/me/settings/${dest}`);
      }
    }
    if (action === 'events' && !hasActionRedirected) {
      try {
        // action should only works for the first route
        dispatch(actionHasRedirected());
        return eventIdFilter ?
          navigate(`/me/events?eventId=${eventIdFilter}`) :
          navigate(`/me/events`);
      } catch (e) {
        console.error('could not parse token', e);
      }
    }
    if ((action === 'pollDetails' || action === 'pollBooking') && !hasActionRedirected) {
      const pendingEventId = parentURL?.searchParams.get('pendingEventId');
      const bookSpot = parentURL?.searchParams.get('bookSpot');
      if (pendingEventId && !pendingEventId.includes('/')) {
        if (action === 'pollBooking' && bookSpot) {
          navigate(`/me/polls/${pendingEventId}?bookSpot=${bookSpot}`, {replace: false});
        } else {
          navigate(`/me/polls/${pendingEventId}`, {replace: false});
        }
        dispatch(actionHasRedirected());
      }
    }
    if ((action === 'clearReservedTimes' || action === 'cancel') && !hasActionRedirected) {
      const pendingEventId = parentURL?.searchParams.get('pendingEventId');
      if (pendingEventId && !pendingEventId.includes('/')) {
        if (action === 'clearReservedTimes') {
          navigate(`/me/appts?pendingEventId=${pendingEventId}&action=clearReservedTimes`, {replace: false});
        } else {
          navigate(`/me/appts?pendingEventId=${pendingEventId}&action=cancel`, {replace: false});
        }
        dispatch(actionHasRedirected());
      }
    }
  }, [navigate, dispatch, action, hasActionRedirected, haveNoAccount,
    listAccountState, pathname, eventIdFilter, skipBindCalendar]);
};

/**
 * get OAuth URL
 * @param {String} service 'google' or 'office365'
 * @return {String} oauthURL
 */
async function generateOAuthURL(service) {
  const response = await patchIntegrationSettings({
    id: 'connections',
    value: [{service}],
  });
  return response.data.oauthUrl;
}

/**
 * get OAuth URL
 * @param {String} service 'googleMeet' or 'teamMeet'
 * @return {String} oauthURL
 */
export const generateMeetOAuthURL = async (service) => {
  const response = await addMeetIntegration(service, {
    email: null,
  });
  return response.data.oauthUrl;
};

export const useConnect3rdParty = () => {
  const [isFetchingOAuthURL, setIsFetchingOAuthURL] = useState(false);
  const dispatch = useDispatch();
  const goToAuth = useCallback(async (service) => {
    if (isFetchingOAuthURL) {
      return;
    }
    setIsFetchingOAuthURL(true);
    // Safari will block open popup window which not triggered by user click gesture,
    // so we need to open a new page in click handler synchronously, and assign href asynchronously.
    const winRef = window.open('', '_blank');
    winRef.document.title = 'Loading...';
    try {
      let url;
      if (service === PAYMENT_INTEGRATION_TYPE.STRIPE) {
        url = (await generatePaymentOnboardingURL({
          payChannel: PAY_CHANNEL.STRIPE,
          payScene: 1, // web payment
          returnUrl: `${window.location.origin || window.origin}/connect_callback/pay`,
        })).result;
      } else if (service === MEET_INTEGRATION_TYPE.GOOGLE || service === MEET_INTEGRATION_TYPE.TEAMS) {
        url = await generateMeetOAuthURL(service);
      } else {
        url = await generateOAuthURL(service);
      }
      winRef.location.href = url;
    } catch (error) {
      dispatch(snackbarActions.addSnackbar({
        message: 'integration.getOAuthURLFailed',
        severity: 'warning',
      }));
      winRef.close();
    } finally {
      setIsFetchingOAuthURL(false);
    }
  }, [dispatch, isFetchingOAuthURL]);

  return {
    isFetchingOAuthURL,
    goToAuth,
  };
};

export const useIframeAutoSize = (notAutoSize) => {
  const scrollableContainer = useRef(null);
  const adjustIframeSize = useCallback((height = '100%') => {
    if (notAutoSize) {
      height = '100%';
    }
    window.parent.postMessage(
      {
        type: 'adjustSize',
        height,
      },
      '*'
    );
  }, [notAutoSize]);

  useEffect(() => {
    // trigger resize immediately when props change
    if (notAutoSize) {
      adjustIframeSize();
    }
  }, [notAutoSize, adjustIframeSize]);

  useEffect(() => {
    let observer;
    let timer;
    if (scrollableContainer.current) {
      if (window.ResizeObserver) {
        observer = new ResizeObserver((entries) => {
          for (const entry of entries) {
            // Add white space to prevent the modal from exceeding the iframe area
            adjustIframeSize(`${entry.contentRect.height + 900}px`);
          }
        });
        observer.observe(scrollableContainer.current);
      } else {
        // if browser does not support resizeObserver then fallback to setInterval
        timer = setInterval(() => {
          adjustIframeSize(getComputedStyle(scrollableContainer.current).height);
        }, 1000);
      }
    }
    return () => {
      observer?.disconnect();
      timer && clearInterval(timer);
    };
  }, [adjustIframeSize]);
  return {
    scrollableContainer,
  };
};

// virtual user will not require user have a zmail account, so we don't user zmail as calendarId anymore
export const formatCalendarId = (jwtPayload) => {
  return getVirtualUserEmail(jwtPayload.uid);
};

// when user sign-out from zoom web portal, we should inform server revoke the token
export const useTokenRevoke = () => {
  useEffect(() => {
    window.addEventListener('unload', () => {
      if (!inWebPortal) {
        return;
      }
      const URL = '/zworkspace/admin/v1/auth/token';
      const tok = getToken();
      if (tok) {
        const payload = JSON.stringify({data: tok});
        fetch(URL, {method: 'POST', headers: {'Content-Type': 'application/json'}, body: payload, keepalive: true});
        navigator.sendBeacon(URL, new Blob([payload], {type: 'application/json'}));
      }
    });
  }, []);
};

export const adjustParentPageStyle = (selector, key, value, important = true) => {
  window.parent.postMessage(
    {
      type: 'adjustStyle',
      selector,
      key,
      value,
      important,
    },
    '*'
  );
};

// Return the vanityUrl if it is enabled and the vanityUrl is truthy, otherwise return the schedulerDomain
export const useSchedulerDomain = (withVanityURLPrefix) => {
  let {vanityUrl, vanityUrlEnabled} = useSelector((state) => state.organizationSettingState);
  if (vanityUrlEnabled && vanityUrl) {
    if (!vanityUrl.startsWith('https')) {
      vanityUrl = `https://${vanityUrl}`;
    }
    if (withVanityURLPrefix) {
      vanityUrl = `${vanityUrl}/zbook`;
    }
    return vanityUrl;
  }
  return schedulerDomain;
};

// generate short URL, type can be 'user' or 'appt' level
export const useCustomLink = (type, appt) => {
  const [customLink, setCustomLink] = useState('');
  const currentUserSlug = useSelector((state) => {
    if (state.hostSettingsState.slug.hasLoaded) {
      return state.hostSettingsState.slug.value;
    } else {
      return null;
    }
  });

  const domain = useSchedulerDomain(true);

  useEffect(() => {
    if (type === 'user' && currentUserSlug) {
      setCustomLink(`${domain}/${currentUserSlug}`);
    }
    if (type === 'appt' && appt) {
      if (appt.bookingLink) {
        if (appt.bookingLink.startsWith('http')) {
          setCustomLink(appt.bookingLink);
        } else {
          setCustomLink([domain, appt.bookingLink].join('/').replace('/zbook/zbook', '/zbook'));
        }
      } else if (isTeamAppt(appt)) {
        // Team Appt types will use the special appt.user slug 'd/<randomTeamSlug>'
        setCustomLink([domain, appt.user, appt.slug].join('/'));
      } else if (currentUserSlug && (appt.slug || appt.id) && !isSharedWithMe(appt)) {
        setCustomLink([domain, currentUserSlug, appt.slug || appt.id].join('/'));
      } else {
        // use the appt organizer's calendarId
        setCustomLink([domain, appt.organizer?.email, appt.slug || appt.id].join('/'));
      }
    }
  }, [appt, currentUserSlug, type, domain]);
  return customLink;
};

export const useInitClientNotification = () => {
  const dispatch = useDispatch();
  useEffect(() => {
    if (!inZoomClient && !inZoomMobileClient) {
      return;
    }
    // the old and new zpns events will coexist in native client until 2024 Q2, so we may receive
    // one event twice at the same time, we can use debounce to ensure the callback to be executed only once.
    const DEBOUNCE_TIME = 200;
    Bus.on(CLIENT_NOTIFICATIONS.onAppointmentChanged, debounce(() => {
      const loc = window.location.pathname;
      if (loc.match('/appts')) {
        // Disable refreshing if admin is currently viewing another user's appts
        const search = new URLSearchParams(window.location.search);
        const userId = search.get('userId');
        const selfUser = getUserInfo();
        if (userId && strEq(userId, selfUser?.userId)) {
          return;
        }
      }
      dispatch(listAppointmentsForCalendar({
        query: {
          orderBy: 'updated',
        },
      }));
    }, DEBOUNCE_TIME));
    Bus.on(CLIENT_NOTIFICATIONS.onConnectionChanged, debounce(() => {
      dispatch(listAccounts());
      const {calendarId} = getUserInfo() || {};
      if (calendarId) {
        // re-query the current user's connection health to clear error banner
        dispatch(validateUserConnections([calendarId.toLowerCase()]));
      }
    }, DEBOUNCE_TIME));
    const onZMailAccountChange = async () => {
      // token will be invalidated when zmail account change, need to request a new one
      await forceRefreshClientToken({
        zmailChange: true,
      });
      dispatch(listAccounts());
    };
    Bus.on(CLIENT_NOTIFICATIONS.onZMailAccountChange, onZMailAccountChange);
    Bus.on(CLIENT_NOTIFICATIONS.onZMailAccountChangeNew, onZMailAccountChange);
    Bus.on(CLIENT_NOTIFICATIONS.onWorkflowChange, debounce(() => {
      dispatch(listWorkflowsForCalendar({}));
    }, DEBOUNCE_TIME));
    Bus.on('refreshToken', ({target}) => {
      token = target.info.sak;
      asyncStats.setToken({token: `Bearer ${token}`});
      asyncStats.report({metricName: METRIC_NAME.CLIENT_REFRESH_TOKEN, isImmediately: true});
    });
    Bus.on(CLIENT_NOTIFICATIONS.onZakTokenRefresh, async () => {
      const {result} = await hybrid.bridge.getInstance().postJsMessage({
        functionName: 'calendarui_getzoombookingaccesstoken',
        params: {},
      });
      zakToken = result.zak;
    });
    Bus.on(CLIENT_NOTIFICATIONS.onManagedAppointmentChange, debounce(() => {
      dispatch(fetchManagedEvents());
    }, DEBOUNCE_TIME));
    Bus.on(CLIENT_NOTIFICATIONS.onPendingEventChange, debounce(() => {
      dispatch(listPendingForCalendar({}));
    }, DEBOUNCE_TIME));
    Bus.on(CLIENT_NOTIFICATIONS.onZPNSCommonCommand, ({target}) => {
      // free trial user upgraded to paid license user
      if (target.result.info.uuid === 'command_zsched_ft_end') {
        const {hasZschedLicense, url} = JSON.parse(target.result.info.detail);
        if (hasZschedLicense && !url) {
          dispatch(updateFreeTrialInfo({
            status: FREE_TRIAL_CONDITION.NON_QUALIFY,
            trialType: 0,
          }));
        }
      }
    });
    Bus.on(CLIENT_NOTIFICATIONS.onFreeTrialInfoChange, ({target}) => {
      dispatch(updateFreeTrialInfo({
        url: target.result.info.zoomSchedulerTrialUrl,
        remainDays: target.result.info.zoomSchedulerTrialRemainDays,
        status: target.result.info.zoomSchedulerFreeTrialStatus,
      }));
    });
    // client transparently forwards all server events to frontend, subsequent new event doesn't require client support
    Bus.on(CLIENT_NOTIFICATIONS.schedulerGenericZPNS, ({target}) => {
      let payload = {};
      try {
        payload = JSON.parse(Base64.decode(target.result.data));
      } catch (error) {
        console.warn('decode zpns data failed');
      }
      switch (payload.type) {
        case 'appointment':
          Bus.emit(CLIENT_NOTIFICATIONS.onAppointmentChanged);
          break;
        case 'setting':
          if (payload.id === 'connections') {
            Bus.emit(CLIENT_NOTIFICATIONS.onConnectionChanged);
          }
          break;
        case 'workflow':
          Bus.emit(CLIENT_NOTIFICATIONS.onWorkflowChange);
          break;
        case 'managedAppointment':
          Bus.emit(CLIENT_NOTIFICATIONS.onManagedAppointmentChange);
          break;
        case 'pendingEvent':
          Bus.emit(CLIENT_NOTIFICATIONS.onPendingEventChange);
          break;
        case 'schedulerGroup':
          Bus.emit(CLIENT_NOTIFICATIONS.onGroupChange);
          break;
        default:
          break;
      }
    });
  }, [dispatch]);
};

export const navigateParentPage = (url) => {
  window.parent.postMessage(
    {
      type: 'navigateTo',
      url,
    },
    '*'
  );
};

export const isTokenExpired = () => {
  try {
    const jwtPayload = readJWTPayload(getToken());
    // Date.now and jwt.exp both use the time elapsed since Jan 1, 1970 00:00:00 UTC. so there is no timezone issue.
    if (Date.now() >= jwtPayload.exp * 1000) {
      return true;
    }
  } catch (error) {}
  return false;
};

// common layer will fetch new token from server
export const forceRefreshClientToken = async (params = {}) => {
  try {
    const {result} = await hybrid.bridge.getInstance().postJsMessage({
      functionName: 'calendarui_refreshzoombookingaccesstoken',
      params,
    });
    if (result.sak) {
      token = result.sak;
      asyncStats.setToken({token: `Bearer ${token}`});
      asyncStats.report({metricName: METRIC_NAME.CLIENT_REFRESH_TOKEN, isImmediately: true});
    }
  } catch (e) {
    console.error('force refresh token failed', e);
  }
};

export const openFeedbackDialog = async () => {
  if (inZoomClient) {
    hybrid.bridge.getInstance().postJsMessage({
      functionName: 'calendarui_openfeedback',
      params: {product: 'scheduler'},
    });
    return;
  }
  const res = await genericRequestFromPortal('showFeedbackDialog', 'feedbackResult');
  if (!res.result) {
    // this is a fallback if dialog open failed
    window.open(SUBMIT_FEEDBACK, '_blank');
  }
};

export const openNewPage = (url) => {
  if (typeof ZoomSchedulerSDKClient?.openDialog === 'function') {
    ZoomSchedulerSDKClient.openDialog(url);
    return;
  }
  window.open(url, '_blank');
};

const getTopLevelDomain = (domain) => {
  const parts = new URL(domain || ZOOM_WEB_DOMAIN).hostname.split('.');
  const joinedParts = parts.slice(parts.length - 2).join('.');
  return `.${joinedParts}`;
};

export const requestFreeTrialExtInfo = async () => {
  if (inZoomClient) {
    document.cookie = `zak=${zakToken}; Path=/; Domain=${getTopLevelDomain()};`;
    const url = `${ZOOM_WEB_DOMAIN}/billing/basic/free_trial/detail?category=ZSCHED&trialType=1`;
    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
    });
    const reponseBody = await response.json();
    return reponseBody.result;
  } else {
    const res = await genericRequestFromPortal('ajax', 'ajaxResult', {
      url: '/billing/basic/free_trial/detail',
      data: {
        category: 'ZSCHED',
        trialType: 1,
      },
    });
    const webRawResponse = JSON.parse(res);
    return webRawResponse.result;
  }
};

// start the trialing and remain days count down
export const optInReverseFreeTrial = async () => {
  if (inZoomClient) {
    // get zakToken first
    await requestTokenFromPortal();
    document.cookie = `zak=${zakToken}; Path=/; Domain=${getTopLevelDomain()};`;
    const url = `${ZOOM_WEB_DOMAIN}/billing/notice/event/report/zsched?eventType=enableZschedFt`;
    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
    });
    const reponseBody = await response.json();
    return reponseBody.result;
  } else {
    const res = await genericRequestFromPortal('ajax', 'ajaxResult', {
      url: '/billing/notice/event/report',
      method: 'POST',
      contentType: 'application/json',
      data: JSON.stringify({
        eventType: 'enableZschedFt',
      }),
    });
    const webRawResponse = JSON.parse(res);
    return webRawResponse.result;
  }
};
